import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Bold, Edit3, Eye, EyeOff, Plus, Star, StarOff, Trash2 } from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import BlogPostContent from '@/components/blog/BlogPostContent';
import { formatBlogDate, getBlogPostPath, resolveBlogExcerpt } from '@/utils/blog';

const emptyPostForm = {
  title: '',
  cover_image_url: '',
  excerpt: '',
  content: '',
  is_published: false,
  is_featured: false,
};

const BlogManagement = ({ currentAdmin }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState(emptyPostForm);
  const contentTextareaRef = useRef(null);

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog/posts`);
      setPosts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Impossible de charger les articles du blog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const filteredPosts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return posts;
    }

    return posts.filter((post) => {
      const haystack = [
        post?.title,
        post?.excerpt,
        post?.author,
        post?.slug,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [posts, searchQuery]);

  const publishedCount = posts.filter((post) => post?.is_published).length;
  const draftCount = posts.length - publishedCount;
  const featuredCount = posts.filter((post) => post?.is_featured).length;

  const openCreateDialog = () => {
    setEditingPost(null);
    setFormData({
      ...emptyPostForm,
      is_published: false,
      is_featured: false,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setFormData({
      title: post?.title || '',
      cover_image_url: post?.cover_image_url || '',
      excerpt: post?.excerpt || '',
      content: post?.content || '',
      is_published: Boolean(post?.is_published),
      is_featured: Boolean(post?.is_featured),
    });
    setDialogOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setFormData((current) => {
      const next = {
        ...current,
        [field]: value,
      };
      if (field === 'is_featured' && value) {
        next.is_published = true;
      }
      if (field === 'is_published' && !value) {
        next.is_featured = false;
      }
      return next;
    });
  };

  const applyBoldToSelection = () => {
    const textarea = contentTextareaRef.current;
    const content = String(formData.content || '');
    const selectionStart = textarea?.selectionStart ?? content.length;
    const selectionEnd = textarea?.selectionEnd ?? content.length;
    const selectedText = content.slice(selectionStart, selectionEnd);
    const fallbackText = 'texte en gras';
    const replacement = `**${selectedText || fallbackText}**`;
    const nextContent = `${content.slice(0, selectionStart)}${replacement}${content.slice(selectionEnd)}`;

    handleFieldChange('content', nextContent);
    window.setTimeout(() => {
      textarea?.focus();
      const nextSelectionStart = selectionStart + 2;
      const nextSelectionEnd = nextSelectionStart + (selectedText || fallbackText).length;
      textarea?.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    if (!formData.content.trim()) {
      toast.error("Le contenu de l'article est obligatoire");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        cover_image_url: formData.cover_image_url || null,
        excerpt: formData.excerpt || null,
        content: formData.content,
        is_published: formData.is_published,
        is_featured: formData.is_featured,
      };

      if (editingPost?.id) {
        await axios.put(`${API}/admin/blog/posts/${editingPost.id}`, payload);
        toast.success('Article mis a jour avec succes');
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload);
        toast.success('Article cree avec succes');
      }

      setDialogOpen(false);
      await loadPosts();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible d'enregistrer l'article");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await axios.patch(`${API}/admin/blog/posts/${post.id}/publish`, {
        is_published: !post.is_published,
      });
      toast.success(post.is_published ? 'Article repasse en brouillon' : 'Article publie avec succes');
      await loadPosts();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible de mettre a jour la publication");
    }
  };

  const handleToggleFeatured = async (post) => {
    try {
      await axios.patch(`${API}/admin/blog/posts/${post.id}/feature`, {
        is_featured: !post.is_featured,
      });
      toast.success(post.is_featured ? "Article retire de la mise en avant" : 'Article mis en avant');
      await loadPosts();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible de mettre a jour l'article en avant");
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Supprimer l'article "${post?.title || 'sans titre'}" ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/blog/posts/${post.id}`);
      toast.success('Article supprime avec succes');
      await loadPosts();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Impossible de supprimer l'article");
    }
  };

  const previewExcerpt = resolveBlogExcerpt(formData);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-red-500">CMS</p>
          <h2 className="text-3xl font-bold text-slate-950">Blog Coli Noki</h2>
          <p className="max-w-2xl text-sm leading-7 text-slate-600">
            Gere les articles publics, la publication et l&apos;article mis en avant affiche sur la landing.
          </p>
          {currentAdmin?.full_name ? (
            <p className="text-xs text-slate-500">Auteur par defaut : {currentAdmin.full_name}</p>
          ) : null}
        </div>

        <Button onClick={openCreateDialog} className="rounded-full bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600">
          <Plus className="mr-2 h-4 w-4" />
          Creer un article
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="rounded-3xl border border-slate-200 bg-white">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-slate-950">{posts.length}</p>
            <p className="mt-1 text-sm text-slate-500">Articles total</p>
          </CardContent>
        </Card>
        <Card className="rounded-3xl border border-slate-200 bg-white">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-green-600">{publishedCount}</p>
            <p className="mt-1 text-sm text-slate-500">Publies</p>
          </CardContent>
        </Card>
        <Card className="rounded-3xl border border-slate-200 bg-white">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-red-600">{featuredCount}</p>
            <p className="mt-1 text-sm text-slate-500">En avant</p>
            <p className="text-xs text-slate-400">{draftCount} brouillon(s)</p>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <CardHeader className="space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>Articles</CardTitle>
              <CardDescription>Un seul article peut etre mis en avant a la fois. Les brouillons restent invisibles publiquement.</CardDescription>
            </div>
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Rechercher un article..."
              className="w-full max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredPosts.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center">
              <p className="text-lg font-semibold text-slate-900">Aucun article a afficher</p>
              <p className="mt-2 text-sm text-slate-500">Creons le premier contenu SEO et marketing pour Coli Noki.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Auteur</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPosts.map((post) => (
                  <TableRow key={post.id}>
                    <TableCell className="max-w-md">
                      <div className="space-y-1">
                        <p className="font-medium text-slate-950">{post.title}</p>
                        <p className="line-clamp-2 text-sm text-slate-500">{resolveBlogExcerpt(post)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={post.is_published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {post.is_published ? 'Publie' : 'Brouillon'}
                        </Badge>
                        {post.is_featured ? <Badge className="bg-red-100 text-red-700">EN AVANT</Badge> : null}
                      </div>
                    </TableCell>
                    <TableCell>{post.author || 'Equipe Coli Noki'}</TableCell>
                    <TableCell>{formatBlogDate(post.updated_at || post.created_at, { includeTime: true })}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{post.slug}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(post)} title="Editer l'article">
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleTogglePublish(post)} title={post.is_published ? 'Repasser en brouillon' : 'Publier'}>
                          {post.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleToggleFeatured(post)} title={post.is_featured ? 'Retirer de la une' : 'Mettre en avant'}>
                          {post.is_featured ? <StarOff className="h-4 w-4" /> : <Star className="h-4 w-4" />}
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => handleDelete(post)} title="Supprimer l'article">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{editingPost ? "Modifier l'article" : 'Creer un article'}</DialogTitle>
            <DialogDescription>
              Utilisez un contenu structure avec des paragraphes. Une ligne vide cree un nouveau paragraphe dans l&apos;article public.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="blog-title">Titre</Label>
                <Input
                  id="blog-title"
                  value={formData.title}
                  onChange={(event) => handleFieldChange('title', event.target.value)}
                  placeholder="Ex. Comment optimiser vos delais de livraison"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="blog-cover-image">Image de couverture</Label>
                <Input
                  id="blog-cover-image"
                  value={formData.cover_image_url}
                  onChange={(event) => handleFieldChange('cover_image_url', event.target.value)}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="blog-excerpt">Extrait</Label>
                <Textarea
                  id="blog-excerpt"
                  value={formData.excerpt}
                  onChange={(event) => handleFieldChange('excerpt', event.target.value)}
                  placeholder="Resume court affiche sur la landing et dans la liste des articles."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="blog-content">Contenu</Label>
                  <Button type="button" variant="outline" size="sm" onClick={applyBoldToSelection}>
                    <Bold className="mr-2 h-4 w-4" />
                    Gras
                  </Button>
                </div>
                <Textarea
                  id="blog-content"
                  ref={contentTextareaRef}
                  value={formData.content}
                  onChange={(event) => handleFieldChange('content', event.target.value)}
                  placeholder="Racontez votre article ici. Selectionnez du texte puis cliquez sur Gras pour ajouter **texte**."
                  rows={14}
                />
                <p className="text-xs text-slate-500">
                  Le gras utilise une syntaxe Markdown limitee (**texte**) rendue en React, sans injection HTML.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="space-y-1">
                    <p className="font-medium text-slate-900">Publier</p>
                    <p className="text-sm leading-6 text-slate-500">L&apos;article devient visible sur /blog et via son lien direct.</p>
                  </div>
                  <Switch checked={formData.is_published} onCheckedChange={(checked) => handleFieldChange('is_published', checked)} />
                </div>

                <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="space-y-1">
                    <p className="font-medium text-slate-900">Mettre en avant</p>
                    <p className="text-sm leading-6 text-slate-500">Un seul article en avant, affiche sur la landing.</p>
                  </div>
                  <Switch checked={formData.is_featured} onCheckedChange={(checked) => handleFieldChange('is_featured', checked)} />
                </div>
              </div>
            </div>

            <div className="space-y-4 rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">Apercu public</p>
                <h3 className="text-2xl font-semibold tracking-tight text-slate-950">
                  {formData.title || 'Titre de votre article'}
                </h3>
                <p className="text-sm text-slate-500">
                  {currentAdmin?.full_name || editingPost?.author || 'Equipe Coli Noki'}
                </p>
              </div>

              {formData.cover_image_url ? (
                <img
                  src={formData.cover_image_url}
                  alt={formData.title || 'Apercu article'}
                  className="h-56 w-full rounded-[1.5rem] object-cover"
                />
              ) : (
                <div className="flex h-56 items-end rounded-[1.5rem] bg-gradient-to-br from-red-500 via-red-600 to-cyan-500 p-5 text-white">
                  <p className="max-w-xs text-sm font-medium uppercase tracking-[0.24em] text-white/80">Coli Noki Blog</p>
                </div>
              )}

              <div className="space-y-3">
                <p className="rounded-2xl bg-white px-4 py-3 text-sm leading-7 text-slate-600">
                  {previewExcerpt || 'Votre extrait apparaitra ici pour teaser le contenu.'}
                </p>
                <div className="rounded-2xl bg-white px-4 py-4">
                  <BlogPostContent content={formData.content} emptyMessage="Le contenu apparaitra ici en mode lecture." />
                </div>
              </div>

              {editingPost?.slug && editingPost?.is_published ? (
                <a
                  href={getBlogPostPath(editingPost.slug)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Voir l&apos;article public
                </a>
              ) : null}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Annuler
            </Button>
            <Button data-testid="save-blog-post-btn" onClick={handleSubmit} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManagement;
